import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';

type TFunction = ReturnType<typeof useTranslation>['t'];

import { SHIFT_DATE_FORMAT, SHIFT_DATE_FORMAT_WITH_YEAR, SHIFT_TIME_FORMAT } from '../constants';
import { Period, Shift, WithFormattedDate } from '../types';

export default function formatOfferItemsDates<TShift extends Shift, TPeriod extends Period>(
    shifts: TShift[] | undefined,
    periods: TPeriod[] | undefined,
    timeZone: string,
    t: TFunction
): WithFormattedDate<TShift | TPeriod>[] {
    const items = shifts?.length ? shifts : periods;

    if (!items) {
        return [];
    }

    const itemsInTz = items.map(scheduleItem => ({
        ...scheduleItem,
        startTz: toZonedTime(scheduleItem.start, timeZone),
        finishTz: toZonedTime(scheduleItem.finish, timeZone),
        acceptedTzStr: 'acceptedDateTimeUtc' in scheduleItem && scheduleItem.acceptedDateTimeUtc
            ? t('dates:distanceToNow', { date: scheduleItem.acceptedDateTimeUtc, tenantTz: timeZone })
            : null
    }));

    const currentYear = toZonedTime(new Date(), timeZone).getFullYear();
    const shouldDisplayYear = itemsInTz.some(scheduleItem =>
        scheduleItem.startTz.getFullYear() !== currentYear ||
        scheduleItem.finishTz.getFullYear() !== currentYear);

    const dayFormat = shouldDisplayYear ? SHIFT_DATE_FORMAT_WITH_YEAR : SHIFT_DATE_FORMAT;

    if (shifts?.length) {
        return itemsInTz.map(shift => ({
            ...shift,
            dateStr: t('dates:custom', { date: shift.startTz, format: dayFormat }),
            startTzStr: format(shift.startTz, SHIFT_TIME_FORMAT),
            finishTzStr: format(shift.finishTz, SHIFT_TIME_FORMAT)
        }));
    }

    return itemsInTz.map((period) => ({
        ...period,
        dateStr: `${ t('dates:custom', { date: period.startTz, format: dayFormat }) } - ${ t('dates:custom', { date: period.finishTz, format: dayFormat }) }`,
        startTzStr: formatPeriodTime((period as Period).timeStart),
        finishTzStr: formatPeriodTime((period as Period).timeFinish)
    }));
}

function formatPeriodTime(time: string | undefined | null) {
    return time ? time.split(':').slice(0, 2).join(':') : null;
}
